import type { AnyType } from './any';
import {
    type ContentMappings,
    type ContentMappingSection,
    type PushContentMappingSection,
    type SenderDetails,
    type TrackingParameters,
} from './campaign';
import {
    type CommunicationChannel,
    type TestDestination,
} from './communications';
import { type LightdashUser } from './user';

export enum TemplateSource {
    SORTMENT = 'SORTMENT',
    SENDGRID = 'SENDGRID',
    FACEBOOK = 'FACEBOOK',
}

export enum ExternalTemplateProvider {
    BRAZE = 'BRAZE',
}

export type BeeFreeAuthResponse = {
    access_token: string;
    v2: boolean;
};

export type Template = {
    id: string;
    organizationId: string;
    projectId: string;
    name: string;
    description: string;
    clonedFromId: string | null;
    createdBy: string;
    createdAt: Date;
    updatedBy: string;
    updatedAt: Date;
    contents: TemplateContent[];
    channel: CommunicationChannel;
    imported: boolean;
    source: TemplateSource;
    consideredInLibrary: boolean;
    metadata: {
        [key: string]: string;
    };
    tags: string[] | undefined;
};

export type TemplateContent = {
    templateId: string;
    version: number;
    projectUuid: string;
    versionName: string | null;
    content: TemplateContentDetails;
    variables: TemplateVariableDetails;
    parsedVariables: ParsedVariable[] | null;
    previewUrl: string | null;
    createdAt: Date;
    createdBy: string;
    updatedAt: Date;
    updatedBy: string;
    contentMappings?: ContentMappings;
    subject?: string;
    preheader?: string;
    trackingParameters?: TrackingParameters;
};

export type TemplateContentDetailsUpdateRequest = Pick<
    TemplateContent,
    'contentMappings' | 'subject' | 'trackingParameters' | 'preheader'
>;

export type TemplateContentWithTemplateName = TemplateContent & {
    templateName: string;
};

export type ParsedVariable = {
    key: string;
    type: string;
    value: string;
};

// Info: Ensure the keys of each template content details are unique due to the limitations of the tsoa
export type TemplateContentDetails =
    | EmailTemplateContentDetails
    | SmsTemplateContentDetails
    | WhatsAppTemplateContentDetails
    | PushTemplateContentDetails;

export type EmailTemplateContentDetails = {
    subject?: string;
    html: string;
    json?: {
        [key: string]: AnyType;
    };
    variables?: TemplateVariableDetails;
    parsedVariables?: ParsedVariable[];
};

export type TemplateTagsUpdateRequest = {
    tags: string[];
};

export type SmsTemplateContentDetails = {
    body: string;
    dltTemplateId?: string;
    unicode?: boolean;
    flash?: boolean;
    variables?: TemplateVariableDetails;
};

export enum WAHeaderType {
    IMAGE = 'image',
    VIDEO = 'video',
    TEXT = 'text',
    DOCUMENT = 'document',
    FILE = 'file',
}

export enum WAButtonType {
    URL = 'url',
    QUICK_REPLY = 'quick_reply',
    COPY = 'copy_url',
    PHONE = 'phone',
}

export type WAHeader = {
    type: WAHeaderType;
    text?: string;
    url?: string;
    sample?: Array<string>;
    file?: string;
};
export type WABody = {
    text: string;
    sample?: Array<string>;
};
export type WAFooter = {
    text: string;
};
export type WAButton = {
    type?: string;
    text?: string;
    title?: string;
    id?: string;
    isDynamic?: string;
    is_dynamic?: string;
    domain?: string;
    url?: string;
    index?: number;
    phone?: string;
    value?: string;
    sample?: string;
};
export type WhatsAppTemplateContentDetails = {
    header: WAHeader;
    body: WABody;
    footer: WAFooter;
    buttons: Array<WAButton>;
    language?: string;
    variables?: TemplateVariableDetails;
};

export type ExternalTemplate = {
    name: string;
    integrationId: string;
    customName: string;
    category: string;
    providerName: string;
    languages: Array<WhatsAppExternalTemplateContentDetails>;
    variables?: TemplateVariableDetails;
};

export type SyncExternalTemplate = {
    integrationId: string;
    providerId: string;
};

export type WhatsAppExternalTemplateContentDetails = {
    language: string;
    content: WhatsAppTemplateContentDetails;
    status: string;
    templateId: string;
};
export type ReducedTemplate = Pick<
    Template,
    | 'id'
    | 'name'
    | 'description'
    | 'createdAt'
    | 'updatedAt'
    | 'channel'
    | 'tags'
> & {
    content: TemplateContentDetails;
    createdBy: Pick<LightdashUser, 'firstName' | 'lastName'>;
    updatedBy: Pick<LightdashUser, 'firstName' | 'lastName'>;
    previewUrl?: string;
};

export type Pagination = {
    total?: number;
    lastPage?: number;
    currentPage: number;
    perPage: number;
    from: number;
    to: number;
};

export type TemplateList = {
    data: ReducedTemplate[] | null;
};

export type TemplateListWithPagination = TemplateList & {
    pagination: Pagination;
};

export type CreateTemplateRequest = Required<
    Pick<
        Template,
        'name' | 'description' | 'channel' | 'consideredInLibrary' | 'tags'
    > &
        Pick<TemplateContent, 'content' | 'previewUrl'>
> &
    Partial<
        Pick<
            TemplateContent,
            'contentMappings' | 'subject' | 'preheader' | 'trackingParameters'
        >
    >;

export type UpdateTemplateRequest = Partial<
    Pick<Template, 'name' | 'description'> & Pick<TemplateContent, 'content'>
>;

export type TemplateModelUpdateTemplateRequest = Partial<Template>;

export type Paginate = {
    perPage: number;
    currentPage: number;
};

export type SearchTemplateFromIntegrationRequest = {
    providerId: string;
    integrationId: string;
    templateIds: string[];
};

export type ImportTemplateFromIntegrationRequest =
    SearchTemplateFromIntegrationRequest & {
        addToLibrary: boolean;
    };

export type Sorting = {
    field: string;
    direction: 'asc' | 'desc';
};
export type WaVariableDetails = {
    [ContentMappingSection.HEADER]?: string[];
    [ContentMappingSection.BODY]?: string[];
    [ContentMappingSection.BUTTONS]?: string[];
};
export type SMSVariableDetails = {
    [ContentMappingSection.BODY]?: string[];
};
export type EmailVariableDetails = {
    [ContentMappingSection.SUBJECT]?: string[];
    [ContentMappingSection.BODY]?: string[];
    [ContentMappingSection.PREHEADER]?: string[];
};
export type PushVariableDetails = {
    [PushContentMappingSection.MESSAGE]?: string[];
    [PushContentMappingSection.TITLE]?: string[];
};
export type TemplateVariableDetails = WaVariableDetails &
    SMSVariableDetails &
    EmailVariableDetails &
    PushVariableDetails;

export type ExtractVariablesResponse = {
    variables: TemplateVariableDetails;
    parsedVariables: ParsedVariable[] | null;
};
export type ProviderTemplateDetails = {
    id: string;
    name: string;
    description: string | undefined;
    updatedAt?: string;
    integrationId?: string;
};

export type EmailTemplateDetails = Pick<Template, 'name'>;
// Need to put things specific to fyno since these external templates are fetched from there only.
export type WATemplateDetails = Pick<Template, 'id'> & {
    integrationId: string;
};

// TODO - Remove this function once we honor the new test flow in journeys
export type TestCampaignRequestv1 = {
    to: Array<TestDestination>;
    channel: CommunicationChannel;
    templateContent?: TemplateContentDetails;
    templateDetails: EmailTemplateDetails | WATemplateDetails;
    mappings: ContentMappings;
    senderDetails?: SenderDetails;
    previewText?: string;
    userId?: string;
    subscriptionGroupId?: string;
    channelIdentifier?: string;
    trackingParameters?: TrackingParameters;
};

export type TestCampaignRequest = {
    to: Array<TestDestination>;
};

export type GetAllProviderTemplateRequest = {
    templateIds: string[];
};

export type GetAllProviderTemplateResponse = {
    templates: ProviderTemplateDetails[];
};

export type FileUploadResponse = {
    url: string;
};

export enum TemplateVariables {
    UNSUBSCRIBE = 'sortment_unsub_url',
}

export enum DefaultWhatsappHeaderVariables {
    IMAGE = 'https://storage.googleapis.com/image_upload_sortment_stg/4cf4e13fdf149d4572f8ffab829ae455aab0500dff9eeba09c41741ebe3eec51',
    VIDEO = 'https://fynodev.s3.ap-south-1.amazonaws.com/uploads/Sample_MP4_480_1_5MG-1726738980574-76224563.mp4',
    FILE = 'https://storage.googleapis.com/image_upload_sortment_prod/f2567ae66f5c46d7593b3cf6673f0a1bbdeb95fc04e088e46234512028abad00',
}

export const DefaultWhatsappHeaderVariablesMap = {
    [WAHeaderType.TEXT]: '',
    [WAHeaderType.IMAGE]: DefaultWhatsappHeaderVariables.IMAGE,
    [WAHeaderType.VIDEO]: DefaultWhatsappHeaderVariables.VIDEO,
    [WAHeaderType.DOCUMENT]: DefaultWhatsappHeaderVariables.FILE,
    [WAHeaderType.FILE]: DefaultWhatsappHeaderVariables.FILE,
};

export const WhatsappHeaderVariable = 'url';

export enum WaVariablesPrefix {
    HEADER = 'header_',
    BODY = 'body_',
    FOOTER = 'footer_',
    BUTTON = 'button_',
}

export enum HandlebarNodeType {
    PROGRAM = 'Program',
    MUSTACHE_STATEMENT = 'MustacheStatement',
    BLOCK_STATEMENT = 'BlockStatement',
    SUB_EXPRESSION = 'SubExpression',
    PATH_EXPRESSION = 'PathExpression',
    NUMBER_LITERAL = 'NumberLiteral',
}

export const WAFilesizeLimits = {
    [WAHeaderType.IMAGE]: 5 * 1024 * 1024,
    [WAHeaderType.VIDEO]: 16 * 1024 * 1024,
    [WAHeaderType.DOCUMENT]: 100 * 1024 * 1024,
    [WAHeaderType.FILE]: 100 * 1024 * 1024,
    [WAHeaderType.TEXT]: 0,
};

export enum EmailTemplateType {
    MARKETING = 'marketing',
    TRANSACTIONAL = 'transactional',
}

export type BeefreeTemplateContext = 'free' | 'premium';
export type BeefreeTemplateType = 'email' | 'sms' | 'push';

export type BeefreeTemplate = {
    categories: string[];
    collections: string;
    context: BeefreeTemplateContext;
    description: string;
    shortDescription: string;
    designer: string;
    id: string;
    isBlank: boolean;
    templateType: BeefreeTemplateType;
    order: string;
    publishedAt: string;
    tags: string[];
    thumbnailLarge: string;
    thumbnail: string;
    title: string;
};

export type BeefreeGetAllTemplatesResponse = {
    count: number;
    next: string;
    previous: string;
    results: BeefreeTemplate[];
    facets: {
        categories: {
            id: string;
            name: string;
            parent: string;
            count: number;
        }[];
        collections: {
            id: string;
            name: string;
            count: number;
        }[];
        designers: {
            id: string;
            name: string;
            count: number;
        }[];
        tags: {
            name: string;
            count: number;
        }[];
    };
};

export type BeefreeGetTemplateByIdResponse = Omit<
    BeefreeTemplate,
    'context'
> & {
    designer: {
        avatarUrl: string;
        description: string;
        shortDescription: string;
        displayName: string;
        id: string;
    };
    htmlData: string;
    htmlUrl: string;
    jsonData: Record<string, AnyType>;
    templateType: BeefreeTemplateType;
    order: string;
    publishedAt: string;
    tags: string[];
    thumbnailLarge: string;
    thumbnail: string;
    title: string;
};

export type BeefreeTemplateCategory = {
    bgColor: string;
    fbColor: string;
    highlighted: boolean;
    icon: string | null;
    id: string;
    image: string | null;
    name: string;
    description: string;
    shortDescription: string;
    parent: string;
};

export type BeefreeTemplateCategoriesResponse = {
    count: number;
    next: string | null;
    previous: string | null;
    results: BeefreeTemplateCategory[];
};

export type BeefreeTemplateCollection = {
    bgColor: string;
    description: string;
    shortDescription: string;
    fgColor: string;
    highlighted: boolean;
    iconUrl: string;
    id: string;
    imageUrl: string;
    name: string;
    order: string;
};

export type BeefreeTemplateCollectionsResponse = {
    count: number;
    next: string | null;
    previous: string | null;
    results: BeefreeTemplateCollection[];
};

export type BeefreeTemplateTag = {
    name: string;
};

export type BeefreeTemplateTagsResponse = {
    results: BeefreeTemplateTag[];
};

export enum TemplateLogicType {
    HANDLEBARS = 'handlebars',
    LIQUID = 'liquid',
}

export type BrazeExportTemplateRequest = {
    template_name: string;
    subject: string;
    body: string;
    plaintext_body?: string;
    preheader?: string;
    tags?: string[];
    should_inline_css?: boolean;
};

export type BrazeUpdateTemplateRequest = BrazeExportTemplateRequest & {
    email_template_id: string;
};

export type BrazeExportTemplateResponse = {
    email_template_id: string;
};

export enum PushDeliveryPriority {
    HIGH = 'high',
    NORMAL = 'normal',
}

export type PushTemplateContentDetails = {
    message: string;
    deliveryPriority: PushDeliveryPriority;
    title?: string;
    actionUrl?: string;
    collapseKey?: string;
    additionalData?: Record<string, AnyType>;
    variables?: TemplateVariableDetails;
};

export enum AiEmailTemplateReferenceType {
    TEMPLATE_LIBRARY = 'template_library',
    CAMPAIGNS = 'campaigns',
    BEEFREE_LIBRARY = 'beefree_library',
    FILE = 'file',
}

export type AiEmailTemplateReference = {
    type: AiEmailTemplateReferenceType;
    value: string;
};

export type AiEmailTemplateRequest = {
    prompt: string;
    targetUsers?: string;
    starterTemplate?: AnyType;
    storyline?: string;
    references?: AiEmailTemplateReference[];
    personalization?: string;
};

export type AiEmailTemplateResponse = {
    template: AnyType;
};

/**
 * Event types for streaming template AI generation
 */
export enum AiEmailTemplateStreamEventType {
    SESSION_CREATED = 'session_created',
    PROGRESS = 'progress',
    TOOL_CALL = 'tool_call',
    TOOL_RESULT = 'tool_result',
    TEXT_DELTA = 'text_delta',
    COMPLETE = 'complete',
    ERROR = 'error',
}

export type AiEmailTemplateStreamEvent =
    | {
          type: AiEmailTemplateStreamEventType.SESSION_CREATED;
          sessionId: string;
      }
    | {
          type: AiEmailTemplateStreamEventType.PROGRESS;
          message: string;
      }
    | {
          type: AiEmailTemplateStreamEventType.TOOL_CALL;
          toolName: string;
          toolCallId?: string;
      }
    | {
          type: AiEmailTemplateStreamEventType.TOOL_RESULT;
          toolName: string;
          toolCallId?: string;
      }
    | {
          type: AiEmailTemplateStreamEventType.TEXT_DELTA;
          delta: string;
      }
    | {
          type: AiEmailTemplateStreamEventType.COMPLETE;
          template: AnyType;
      }
    | {
          type: AiEmailTemplateStreamEventType.ERROR;
          error: string;
      };

export enum AiToolFriendlyMessage {
    // Sortment tools
    SORTMENT_BEEFREE_TOOL = 'Generating template...',
    SORTMENT_GET_SRT_TEMPLATE = 'Fetching template from library...',
    SORTMENT_GET_BEEFREE_TEMPLATE = 'Fetching community template...',

    // Beefree structure tools
    BEEFREE_GET_CONTENT_HIERARCHY = 'Analyzing template structure...',
    BEEFREE_GET_SELECTED = 'Checking selected element...',
    BEEFREE_GET_ELEMENT_DETAILS = 'Getting element details...',

    // Beefree section/column tools
    BEEFREE_ADD_SECTION = 'Adding new section...',
    BEEFREE_UPDATE_SECTION_STYLE = 'Updating section style...',
    BEEFREE_UPDATE_COLUMN_STYLE = 'Updating column style...',
    BEEFREE_DELETE_ELEMENT = 'Removing element...',

    // Beefree content add tools
    BEEFREE_ADD_TITLE = 'Adding title...',
    BEEFREE_ADD_PARAGRAPH = 'Adding paragraph...',
    BEEFREE_ADD_BUTTON = 'Adding button...',
    BEEFREE_ADD_IMAGE = 'Adding image...',
    BEEFREE_ADD_LIST = 'Adding list...',
    BEEFREE_ADD_DIVIDER = 'Adding divider...',
    BEEFREE_ADD_SPACER = 'Adding spacer...',
    BEEFREE_ADD_MENU = 'Adding menu...',
    BEEFREE_ADD_SOCIAL = 'Adding social links...',
    BEEFREE_ADD_HTML = 'Adding HTML block...',
    BEEFREE_ADD_VIDEO = 'Adding video...',
    BEEFREE_ADD_ICONS = 'Adding icons...',

    // Beefree content update tools
    BEEFREE_UPDATE_TITLE = 'Updating title...',
    BEEFREE_UPDATE_PARAGRAPH = 'Updating paragraph...',
    BEEFREE_UPDATE_BUTTON = 'Updating button...',
    BEEFREE_UPDATE_IMAGE = 'Updating image...',
    BEEFREE_UPDATE_LIST = 'Updating list...',
    BEEFREE_UPDATE_DIVIDER = 'Updating divider...',
    BEEFREE_UPDATE_SPACER = 'Updating spacer...',
    BEEFREE_UPDATE_MENU = 'Updating menu...',
    BEEFREE_UPDATE_SOCIAL = 'Updating social links...',
    BEEFREE_UPDATE_HTML = 'Updating HTML block...',
    BEEFREE_UPDATE_VIDEO = 'Updating video...',
    BEEFREE_UPDATE_ICONS = 'Updating icons...',

    // Beefree template management tools
    BEEFREE_LIST_TEMPLATES = 'Searching templates...',
    BEEFREE_CLONE_TEMPLATE = 'Cloning template...',
    BEEFREE_CHECK_TEMPLATE = 'Validating template...',
    BEEFREE_SET_EMAIL_METADATA = 'Setting email metadata...',

    // Web fetch tool
    WEBFETCHSAFE = 'Fetching reference content...',

    // Default fallback
    DEFAULT = 'Processing...',
}
